import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

type QRScannerProps = {
  onScanSuccess: (decodedText: string) => void | Promise<void>;
  onScanFailure?: (error: string) => void;
  hasScannedError?: boolean;
  setHasScannedError?: (value: boolean) => void;
  showScanner?: boolean;
  restartTrigger?: number;
};

type CameraPermissionState =
  | "checking"
  | "prompt"
  | "requesting"
  | "granted"
  | "denied";

const SCAN_CONFIG = {
  fps: 10,
  qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
    const edge = Math.min(viewfinderWidth, viewfinderHeight);
    const size = Math.max(50, Math.floor(edge * 0.7));
    return { width: size, height: size };
  },
  disableFlip: true,
};

const QRScanner = ({
  onScanSuccess,
  hasScannedError,
  setHasScannedError,
  showScanner = true,
  restartTrigger = 0,
}: QRScannerProps) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const scannerInstance = useRef<Html5Qrcode | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const scannerActive = useRef(false);
  const runIdRef = useRef(0);
  const hasScannedRef = useRef(false);
  const scanBlockedRef = useRef(false);
  const onScanSuccessRef = useRef(onScanSuccess);

  const [redLine, setRedLine] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [scanTimedOut, setScanTimedOut] = useState(false);
  const [cameraPermission, setCameraPermission] =
    useState<CameraPermissionState>("checking");

  onScanSuccessRef.current = onScanSuccess;

  const stopIdleTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    stopIdleTimer();
    timerRef.current = setInterval(() => {
      if (!hasScannedRef.current) {
        setScanTimedOut(true);
      }
    }, 20000);
  }, [stopIdleTimer]);

  const destroyScanner = useCallback(async () => {
    runIdRef.current += 1;
    stopIdleTimer();
    scannerActive.current = false;
    const instance = scannerInstance.current;
    scannerInstance.current = null;
    if (!instance) return;

    try {
      if (instance.isScanning) {
        await instance.stop();
      }
    } catch {
      // already stopped
    }

    try {
      instance.clear();
    } catch {
      // already cleared or unmounted
    }
  }, [stopIdleTimer]);

  const initScanner = useCallback(async () => {
    if (!document.getElementById("qr-reader")) return;

    const runId = ++runIdRef.current;
    setPermissionDenied(false);

    const scanner = new Html5Qrcode("qr-reader", {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      verbose: false,
    });
    scannerInstance.current = scanner;

    try {
      const cameras = await Html5Qrcode.getCameras();
      if (runId !== runIdRef.current) {
        try {
          scanner.clear();
        } catch {
          // a newer scan already owns the view
        }
        return;
      }
      if (!cameras.length) {
        setPermissionDenied(true);
        return;
      }

      const preferred =
        cameras.find((camera) => /back|rear|environment/i.test(camera.label)) ||
        cameras[0];

      scannerActive.current = true;
      await scanner.start(
        preferred.id,
        SCAN_CONFIG,
        (decodedText) => {
          if (hasScannedRef.current || scanBlockedRef.current) return;

          hasScannedRef.current = true;
          setRedLine(false);
          stopIdleTimer();
          onScanSuccessRef.current(decodedText);

          scanner
            .stop()
            .catch(() => {})
            .finally(() => {
              try {
                scanner.clear();
              } catch {
                // already cleared
              }
              if (scannerInstance.current === scanner) {
                scannerInstance.current = null;
              }
              scannerActive.current = false;
            });
        },
        () => {
          // html5-qrcode calls this for ordinary per-frame decode misses.
          // Do not surface it as an invalid ticket before a QR is scanned.
        }
      );

      if (runId !== runIdRef.current) {
        if (scanner.isScanning) await scanner.stop();
        scanner.clear();
        return;
      }

      startIdleTimer();
    } catch (err) {
      console.error("Camera access denied:", err);
      if (runId !== runIdRef.current) return;
      if (scannerInstance.current === scanner) {
        scannerInstance.current = null;
        scannerActive.current = false;
      }
      try {
        if (scanner.isScanning) await scanner.stop();
      } catch {
        // not started
      }
      try {
        scanner.clear();
      } catch {
        // already cleared
      }
      setPermissionDenied(true);
    }
  }, [startIdleTimer, stopIdleTimer]);

  const resumeScanning = useCallback(async () => {
    hasScannedRef.current = false;
    scanBlockedRef.current = false;
    setScanTimedOut(false);
    setRedLine(true);
    await destroyScanner();
    initScanner();
  }, [destroyScanner, initScanner]);

  const handleClickToReactivate = () => {
    setHasScannedError?.(false);
    resumeScanning();
  };

  const requestCameraPermission = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionDenied(true);
      setCameraPermission("denied");
      return;
    }

    setCameraPermission("requesting");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionDenied(false);
      setCameraPermission("granted");
    } catch (error) {
      console.error("Camera permission denied:", error);
      setPermissionDenied(true);
      setCameraPermission("denied");
    }
  };

  useEffect(() => {
    if (!showScanner) return;

    let cancelled = false;
    let permissionStatus: PermissionStatus | undefined;

    const checkPermission = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) {
          setPermissionDenied(true);
          setCameraPermission("denied");
        }
        return;
      }

      try {
        permissionStatus = await navigator.permissions?.query({
          name: "camera" as PermissionName,
        });

        if (cancelled) return;

        const updatePermission = () => {
          if (!permissionStatus) return;
          setPermissionDenied(permissionStatus.state === "denied");
          setCameraPermission(
            permissionStatus.state === "granted"
              ? "granted"
              : permissionStatus.state === "denied"
                ? "denied"
                : "prompt"
          );
        };

        if (permissionStatus) {
          updatePermission();
          permissionStatus.onchange = updatePermission;
        } else {
          setCameraPermission("prompt");
        }
      } catch {
        if (!cancelled) setCameraPermission("prompt");
      }
    };

    checkPermission();

    return () => {
      cancelled = true;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, [showScanner]);

  useEffect(() => {
    if (!showScanner || cameraPermission !== "granted") {
      destroyScanner();
      return;
    }

    hasScannedRef.current = false;
    setScanTimedOut(false);
    setRedLine(true);
    setPermissionDenied(false);
    initScanner();

    return () => {
      destroyScanner();
    };
  }, [cameraPermission, showScanner, destroyScanner, initScanner]);

  useEffect(() => {
    if (restartTrigger < 1) return;
    resumeScanning();
  }, [restartTrigger, resumeScanning]);

  const showScanAgain = Boolean(hasScannedError || scanTimedOut);

  useEffect(() => {
    scanBlockedRef.current = showScanAgain;
    const scanner = scannerInstance.current;
    if (!scanner?.isScanning) return;

    try {
      if (showScanAgain) {
        stopIdleTimer();
        scanner.pause(false);
      }
    } catch {
      // scanner not in a pausable state
    }
  }, [showScanAgain, stopIdleTimer]);

  return (
    <>
      <div className="position-relative">
        {cameraPermission === "checking" ? (
          <div className="cameraPermissionPrompt">
            <p>Checking camera permission…</p>
          </div>
        ) : cameraPermission === "prompt" ||
          cameraPermission === "requesting" ? (
          <div className="cameraPermissionPrompt">
            <div className="cameraPermissionIcon" aria-hidden="true">
              📷
            </div>
            <p className="cameraPermissionTitle">Camera access required</p>
            <p>
              Allow camera access to scan the QR code printed on your ticket.
            </p>
            <button
              type="button"
              className="cameraPermissionButton"
              onClick={requestCameraPermission}
              disabled={cameraPermission === "requesting"}
            >
              {cameraPermission === "requesting"
                ? "REQUESTING…"
                : "ENABLE CAMERA"}
            </button>
          </div>
        ) : permissionDenied ? (
          <div className="permissionDenied text-start">
            <p className="deniedTxt">
              Camera access was denied. Please enable camera permissions in your
              browser settings.
            </p>
            <p className="mb-2">For example:</p>
            <p className="mb-2">
              - In Chrome: Click the icon in the address bar → Site Settings →
              Allow Camera
            </p>
            <p className="mb-2">
              - In Safari: Preferences → Websites → Camera → Allow
            </p>
            <button
              type="button"
              className="cameraPermissionButton"
              onClick={requestCameraPermission}
            >
              TRY AGAIN
            </button>
          </div>
        ) : (
          <div
            id="qr-reader"
            ref={scannerRef}
            style={{
              filter: scanTimedOut ? "blur(4px)" : "none",
            }}
          />
        )}
        {cameraPermission === "granted" &&
          !permissionDenied &&
          redLine &&
          !scanTimedOut && (
          <div className="scanLine" />
        )}
      </div>
      {showScanAgain && (
        <>
          <div className="overlay" />
          <button
            className="btnPrimaryScanAgian"
            onClick={handleClickToReactivate}
          >
            SCAN AGAIN
          </button>
        </>
      )}
    </>
  );
};

export default QRScanner;
